import { useEffect } from 'react';
import { useDispatch, useFileCollection } from '@witmetrics/data-store';
import { fetchCollectionContents } from '@/utils/files';
import { useAppState } from '@/providers/AppStateProvider';
import { useTab } from '@/hooks/useTab';
import { useId } from '@/hooks/useId';
import { useArray } from '@/hooks/useArray';
import TabsBlock from '@/components/TabsBlock';
import Card from '@/components/Card';
import IconButton from '@/components/IconButton';
import type { FileCollectionPath } from '@/components/FileCollectionCrumbs';
import { CloseIcon, MenuIcon } from '@/icons';
import CollectionOptionsMenu from './CollectionOptionsMenu';
import FilesTab from './FilesTab';
import NotesTab from './NotesTab';
import CondensedFilesList from './CondensedFilesList';
import type { CollectionTab } from './types';
import { classes } from './utils';

export type CollectionCardProps = {
  baseCollectionId: number;
  activeFileId?: number;
  condensed?: boolean;
  onChangeTab?: (tab: CollectionTab) => void;
  onHideSidebar?: () => void;
  onOpenFile: (fileId: number) => void;
  onClose: () => void;
};

export default function CollectionCard({
  baseCollectionId,
  activeFileId,
  condensed,
  onChangeTab,
  onHideSidebar,
  onOpenFile,
  onClose,
}: CollectionCardProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const [activeCollectionId, setActiveCollectionId] = useId(baseCollectionId);
  const activeCollection = useFileCollection(activeCollectionId);
  const [activeTab, setActiveTab] = useTab<CollectionTab>('files');
  const [crumbs, setCrumbs] = useArray<FileCollectionPath>();

  useEffect(() => {
    if (activeCollectionId) handleFetchContent();
  }, [activeCollectionId]);

  const handleFetchContent = async () => {
    try {
      if (!activeCollectionId) throw Error('Missing activeCollectionId');
      const data = await fetchCollectionContents(activeCollectionId);
      dispatch.addFileCollections(data.fileCollections.results);
      dispatch.addFileCollectionFiles(data.files.results);
      setCrumbs(data.path);
    } catch (err) {
      onApiError(err, 'Error fetching file collection', () =>
        handleFetchContent()
      );
    }
  };

  const handleSelectCrumb = (collectionId: number | undefined) => {
    setActiveCollectionId(collectionId ?? baseCollectionId);
  };

  const handleChangeTab = (tab: CollectionTab) => {
    setActiveTab(tab);
    onChangeTab?.(tab);
  };

  const handleAfterDelete = () => {
    // Deleting the collection the overlay opened on leaves nothing to show, so close the tile (which closes the overlay when it's the last one).
    if (activeCollectionId === baseCollectionId) return onClose();
    // Otherwise drop back to the deleted folder's parent.
    setActiveCollectionId(
      activeCollection?.parentFileCollectionId ?? baseCollectionId
    );
  };

  if (!activeCollectionId) return null; // TODO

  if (condensed) {
    return (
      <Card className={classes.condensedCard}>
        <CondensedFilesList
          activeCollectionId={activeCollectionId}
          baseCollectionId={baseCollectionId}
          activeFileId={activeFileId}
          crumbs={crumbs}
          onSelectFile={onOpenFile}
          onSelectCollection={setActiveCollectionId}
        />
      </Card>
    );
  }

  const tabs = [
    <FilesTab
      key="files"
      activeFileId={activeFileId}
      activeCollectionId={activeCollectionId}
      baseCollectionId={baseCollectionId}
      name="files"
      label="Files"
      crumbs={crumbs}
      onSelectFile={onOpenFile}
      onSelectCollection={setActiveCollectionId}
      onSelectCrumb={handleSelectCrumb}
    />,
  ];
  if (activeFileId) {
    tabs.push(
      <NotesTab
        key="notes"
        activeFileId={activeFileId}
        name="notes"
        label="Notes"
      />
    );
  }

  return (
    <Card className={classes.card}>
      <div className={classes.headerControls}>
        {!activeFileId && (
          <IconButton
            titleArrow
            size="small"
            title="Close"
            titlePosition="left"
            onClick={onClose}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        )}
        <CollectionOptionsMenu
          fileCollectionId={activeCollectionId}
          onDelete={handleAfterDelete}
        />
      </div>
      <TabsBlock<CollectionTab[]>
        className={classes.tabs}
        name="collection-card-tabs"
        defaultTab={activeTab}
        bodyClassName={classes.tabsBody}
        onChange={handleChangeTab}>
        {tabs}
      </TabsBlock>
      {onHideSidebar && (
        <div className={classes.footer}>
          <IconButton
            title="Hide files"
            titlePosition="top"
            onClick={onHideSidebar}>
            <MenuIcon />
          </IconButton>
        </div>
      )}
    </Card>
  );
}
